import React, { useEffect, useState } from "react";
import { TabNavigation } from "../../component/navigation/TabNavigation";
import { Breadcrumb } from "../../component/navigation/Breadcrumb";
import { DomainOverview } from "../../component/Layout/Domain/DomainOverview";
import { SubdomainDetail } from "../../component/Layout/Domain/SubdomainDetail";
import { domainData1 } from "../../data/dataDomain/domainData";
import axiosInstance from "../../axiosInstanceSetup";
import { Loader2 } from "lucide-react";
import { ProductPage } from "../../component/Layout/Domain/ProductPage";
import { AssetsSummary } from "../../component/Layout/Domain/AssetsSummary";
import {AssetsDetails} from "../../component/Layout/Domain/AssetsDetails";
import heartLoading from "../../assests/heartLoading.gif"
import { useSearchParams, useNavigate } from "react-router-dom";
import preloader from "../../assests/preloader1.gif";


function LandingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [navigation, setNavigation] = useState({});
  const [domainData, setDomainData] = useState();
  const [loading, setLoading] = useState(false);
  const [selectedProductDetails, setSelectedProductDetails] = useState(null);
  const [profilingResult, setProfilingResult] = useState(null);
  const [dqResult, setDQResult] = useState(null);
  
  // Filter states - persisted at parent level
  const [appliedFilters, setAppliedFilters] = useState({
    regions: [],
    dataLayer: []
  });
  
  // Regions from API - shared across all levels
  const [regions, setRegions] = useState([]);

  const fetchDomainData = async (regions = [], dataLayers = []) => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams();
      
      // Add multiple regions as separate query parameters
      regions.forEach(region => {
        params.append('region', region);
      });
      
      // Add multiple data layers as separate query parameters
      if (Array.isArray(dataLayers)) {
        dataLayers.forEach(layer => {
          if (layer && layer.trim()) {
            params.append('data_layer', layer);
          }
        });
      }
      
      const queryString = params.toString();
      const url = queryString 
        ? `${process.env.REACT_APP_API_URL}/domaindata?${queryString}`
        : `${process.env.REACT_APP_API_URL}/domaindata`;
      
      const response = await axiosInstance.get(url);

      if (response.status === 200) {
        const DomainData = response?.data?.data;
        setDomainData(DomainData);
        
        // Check URL params for navigation state
        const l1 = searchParams.get('l1');
        const l2 = searchParams.get('l2');
        const l3 = searchParams.get('l3');
        const l4 = searchParams.get('l4');
        const l5 = searchParams.get('l5');
        
        if (l1 && l2 && l3 && l4 && l5) {
          // Restore to L5 level
          setNavigation({
            level: "L5",
            selectedL1: l1,
            selectedL2: l2,
            selectedL3: l3,
            selectedL4: l4,
            selectedL5: l5,
          });
          // Fetch product details for L4
          await getProductDetails(l4);
        } else if (l1 && l2 && l3 && l4) {
          // Restore to L4 level
          setNavigation({
            level: "L4",
            selectedL1: l1,
            selectedL2: l2,
            selectedL3: l3,
            selectedL4: l4,
          });
          // Fetch product details for L4
          await getProductDetails(l4);
        } else if (l1 && l2 && l3) {
          // Restore to L3 level
          setNavigation({
            level: "L3",
            selectedL1: l1,
            selectedL2: l2,
            selectedL3: l3,
          });
        } else if (l1 && l2) {
          // Restore to L2 level
          setNavigation({
            level: "L2",
            selectedL1: l1,
            selectedL2: l2,
          });
        } else if (l1) {
          // Restore to L1 level
          setNavigation({
            level: "L1",
            selectedL1: l1,
          });
        } else {
          // Default to first parent domain
          const firstParent = DomainData && DomainData[0] && DomainData[0].parent_L1;
          setNavigation({
            level: "L1",
            selectedL1: `${firstParent}`,
          });
        }

        console.log("data of domain", l1 || DomainData[0]?.parent_L1);
        setLoading(false);
      }
    } catch (error) {
      console.error("getting error while fetching the domain information");
      setLoading(false);
    }
  };

  // Helper function to update URL parameters
  const updateURLParams = (newNavigation) => {
    const params = new URLSearchParams();
    if (newNavigation.selectedL1) params.set('l1', newNavigation.selectedL1);
    if (newNavigation.selectedL2) params.set('l2', newNavigation.selectedL2);
    if (newNavigation.selectedL3) params.set('l3', newNavigation.selectedL3);
    if (newNavigation.selectedL4) params.set('l4', newNavigation.selectedL4);
    if (newNavigation.selectedL5) params.set('l5', newNavigation.selectedL5);
    
    setSearchParams(params);
  };

  const getProductDetails = async(productName) => {
    try{
      setLoading(true)
      
      const response = await axiosInstance.get(`${process.env.REACT_APP_API_URL}/get-product-submission/${productName}`)

      if(response.status === 200 || response.status === 201){
        console.log("response", response)
        console.log("response data", response.data)
        setSelectedProductDetails(response.data)

        const profilingResponse = await axiosInstance.get(
          `${process.env.REACT_APP_API_URL}/profiling_results/${response.data.productId}`);

        if (profilingResponse.status === 200) {
          setProfilingResult(profilingResponse?.data?.profiling_results);
        }

        const dqResponse = await axiosInstance.get(
          `${process.env.REACT_APP_API_URL}/runQuality/ruleStatus/${response.data.productId}`);

        if (dqResponse.status === 200) {
          setDQResult(dqResponse?.data);
        }

        setLoading(false)
      }
    
    }catch(e){
      console.error("Error fetching product details:", e);
      setLoading(false)
      // Add user notification here if you have toast system
    }
  }

  // Handle filter changes from DomainOverview
  const handleFilterChange = (regions, dataLayers) => {
    // Save filters in parent state
    setAppliedFilters({
      regions: regions,
      dataLayer: dataLayers
    });
    // Fetch data with filters
    fetchDomainData(regions, dataLayers);
  };

  useEffect(() => {
    fetchDomainData()
  },[])

  // Fetch regions from API on component mount
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const response = await axiosInstance.get(`${process.env.REACT_APP_API_URL}/parent-region`);
        setRegions(response?.data?.parent_regions || []);
      } catch (error) {
        console.error("Error fetching regions:", error);
        setRegions([]);
      }
    };
    fetchRegions().catch(console.error);
  }, []);

  // Create tabs from domain data
  const tabs =
    domainData &&
    domainData.map((domain) => ({
      id: domain.parent_L1,
      label: domain.parent_L1,
      count: domain.domains_L2.length,
    }));

  // Get current domain
  const currentDomain =
    domainData && domainData.find((d) => d.parent_L1 === navigation.selectedL1);
  // console.log("current domain", currentDomain)
  const currentL2Subdomain = currentDomain?.domains_L2.find(
    (sub) => sub.name === navigation.selectedL2
  );
  // console.log("currentL2Subdomain", currentL2Subdomain)
  const currentL3Subdomain = currentL2Subdomain?.subdomains_L3.find(
    (sub) => sub.name === navigation.selectedL3
  );

  const currentL4Subdomain = currentL3Subdomain?.Products.find(
    (sub) => sub.product_name === navigation.selectedL4
  );

  const currentL5Subdomain = currentL4Subdomain?.product_assets_detail.find(
    (sub) => sub.data_asset_name === navigation.selectedL5
  );


  // Navigation handlers
  const handleTabChange = (tabId) => {
    const newNavigation = {
      level: "L1",
      selectedL1: tabId,
    };
    setNavigation(newNavigation);
    updateURLParams(newNavigation);
  };

  // Fixed back navigation handlers - properly clear deeper level selections
  const handleBackToL1 = () => {
    const newNavigation = {
      level: "L1",
      selectedL1: navigation.selectedL1,
      // Clear all deeper selections
      selectedL2: undefined,
      selectedL3: undefined,
      selectedL4: undefined,
      selectedL5: undefined,
    };
    setNavigation(newNavigation);
    updateURLParams(newNavigation);
  };

  const handleBackToL2 = () => {
    const newNavigation = {
      level: "L2",
      selectedL1: navigation.selectedL1,
      selectedL2: navigation.selectedL2,
      // Clear deeper selections
      selectedL3: undefined,
      selectedL4: undefined,
      selectedL5: undefined,
    };
    setNavigation(newNavigation);
    updateURLParams(newNavigation);
  };

  const handleBackToL3 = () => {
    const newNavigation = {
      level: "L3",
      selectedL1: navigation.selectedL1,
      selectedL2: navigation.selectedL2,
      selectedL3: navigation.selectedL3,
      // Clear deeper selections
      selectedL4: undefined,
      selectedL5: undefined,
    };
    setNavigation(newNavigation);
    updateURLParams(newNavigation);
  };

  const handleBackToL4 = () => {
    const newNavigation = {
      level: "L4",
      selectedL1: navigation.selectedL1,
      selectedL2: navigation.selectedL2,
      selectedL3: navigation.selectedL3,
      selectedL4: navigation.selectedL4,
      // Clear deeper selections
      selectedL5: undefined,
    };
    setNavigation(newNavigation);
    updateURLParams(newNavigation);
  };

  const handleBackToL5 = () => {
    const newNavigation = {
      level: "L5",
      selectedL1: navigation.selectedL1,
      selectedL2: navigation.selectedL2,
      selectedL3: navigation.selectedL3,
      selectedL4: navigation.selectedL4,
      selectedL5: navigation.selectedL5,
    };
    setNavigation(newNavigation);
    updateURLParams(newNavigation);
  };

  // Forward navigation handlers
  const handleSubdomainClick = (subdomainName) => {
    const newNavigation = {
      level: "L2",
      selectedL1: navigation.selectedL1,
      selectedL2: subdomainName,
      // Clear deeper selections when moving forward
      selectedL3: undefined,
      selectedL4: undefined,
      selectedL5: undefined,
    };
    setNavigation(newNavigation);
    updateURLParams(newNavigation);
  };

  const handleL3Click = (l3Name) => {
    const newNavigation = {
      level: "L3",
      selectedL1: navigation.selectedL1,
      selectedL2: navigation.selectedL2,
      selectedL3: l3Name,
      // Clear deeper selections
      selectedL4: undefined,
      selectedL5: undefined,
    };
    setNavigation(newNavigation);
    updateURLParams(newNavigation);
  };

  const handleL4Click = (l4Name) => {
    console.log("L4 Clicked", l4Name);
    const newNavigation = {
      level: "L4",
      selectedL1: navigation.selectedL1,
      selectedL2: navigation.selectedL2,
      selectedL3: navigation.selectedL3,
      selectedL4: l4Name,
      // Clear deeper selections
      selectedL5: undefined,
    };
    setNavigation(newNavigation);
    updateURLParams(newNavigation);
    getProductDetails(l4Name);
  };

  const handleL5Click = (l5Name) => {
    console.log("L5 Clicked", l5Name);
    const newNavigation = {
      level: "L5",
      selectedL1: navigation.selectedL1,
      selectedL2: navigation.selectedL2,
      selectedL3: navigation.selectedL3,
      selectedL4: navigation.selectedL4,
      selectedL5: l5Name,
    };
    setNavigation(newNavigation);
    updateURLParams(newNavigation);
  };

  console.log("navigation", navigation);

  // Breadcrumb items with improved navigation
  const getBreadcrumbItems = () => {
    const items = [];

    if (navigation.selectedL1) {
      items.push({
        label: navigation.selectedL1,
        onClick: navigation.level !== "L1" ? handleBackToL1 : undefined,
      });
    }

    if (navigation.selectedL2) {
      items.push({
        label: navigation.selectedL2,
        onClick: navigation.level !== "L2" ? handleBackToL2 : undefined,
      });
    }

    if (navigation.selectedL3) {
      items.push({
        label: navigation.selectedL3,
        onClick: navigation.level !== "L3" ? handleBackToL3 : undefined,
      });
    }

    if (navigation.selectedL4) {
      items.push({
        label: navigation.selectedL4,
        onClick: navigation.level !== "L4" ? handleBackToL4 : undefined,
      });
    }

    if (navigation.selectedL5) {
      items.push({
        label: navigation.selectedL5,
        onClick: navigation.level !== "L5" ? handleBackToL5 : undefined,
      });
    }

    return items;
  };

  useEffect(() => {
    console.log("useEffect called for domainData");
  }, [navigation, currentDomain, currentL2Subdomain, currentL3Subdomain, currentL4Subdomain, currentL5Subdomain]);

  if (loading) {
    return (
        <div className="relative inset-0 bg-white flex items-center justify-center z-50 w-full h-full">
                    <div className="flex flex-col items-center space-y-3">
                      <img src={preloader} alt="Loading..." className="h-28" />
                      <span className="text-gray-700 text-[20px]">
                      Hang tight! The resources are loading...
                      </span>
                    </div>
                  </div>
    );
  }

  return (
    <div className="bg-white relative rounded-xl ">
      <div className="mx-auto">
        {/* Header */}
        {/* <div className="mb-2">
          <h1 className="text-4xl font-bold text-gray-900">
            Domain Management
          </h1>
          <p className="text-xl text-gray-600">
            Comprehensive view of your data domains, sub-domains, and assets
          </p>
        </div> */}

        {/* Tab Navigation - Only show on L1 level */}
        {navigation.level === "L1" && (
          <TabNavigation
            tabs={tabs}
            activeTab={navigation.selectedL1 || "Product"}
            onTabChange={handleTabChange}
          />
        )}

        {/* Breadcrumb - Show on L2 and deeper levels */}
        {navigation.level !== "L1" && (
          <Breadcrumb items={getBreadcrumbItems()} />
        )}

        {/* Content */}
<div
  className={`space-y-8 ${
    navigation.level !== "L5" ? "ml-[20px] mr-[20px]" : "bg-[#F7F7F7] pl-4 pr-5"
  }`}
>          {navigation.level === "L1" && currentDomain && (
            <DomainOverview
              domain={currentDomain}
              onSubdomainClick={handleSubdomainClick}
              onFilterChange={handleFilterChange}
              appliedFilters={appliedFilters}
            />
          )}

          {navigation.level === "L2" && currentL2Subdomain && (
            <SubdomainDetail
              subdomain={currentL2Subdomain}
              onBack={handleBackToL1}
              onL3Click={handleL3Click}
              regions={regions}
              appliedFilters={appliedFilters}
              onFilterChange={handleFilterChange}
            />
          )}

          {navigation.level === "L3" && currentL3Subdomain && (
            <ProductPage
              subdomain={currentL3Subdomain}
              onBack={handleBackToL2}
              onL4Click={handleL4Click}
              regions={regions}
              appliedFilters={appliedFilters}
              onFilterChange={handleFilterChange}
            />
          )}

          {navigation.level === "L4" && currentL4Subdomain && (
            <AssetsSummary
              subdomain={currentL4Subdomain}
              onBack={handleBackToL3}
              onL5Click={handleL5Click}
            />
          )}

          {navigation.level === "L5" && currentL5Subdomain && (
            <AssetsDetails
              subdomain={currentL5Subdomain}
              productDetails={selectedProductDetails}
              profilingResult={profilingResult}
              dqResult={dqResult}
              onBack={handleBackToL4}
              onL5Click={handleL5Click}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default LandingPage;